import { AsyncSelect } from '@waylay/react-components'
import { get, isEmpty, noop } from 'lodash-es'

import { fetchResources } from '../../hooks/useResourcesList'
import React, { Fragment, useState, useCallback } from 'react'

export const findResources = async (filter: string) => {
  const resources = await fetchResources({ filter })

  return get(resources, '_embedded.values', []).map(resource => ({
    ...resource,
    value: resource.id,
  }))
}

interface IResourceSelect extends Omit<React.HTMLAttributes<any>, 'onChange'> {
  name?: string
  resource?: string
  loadOptions?: Function
  allowAnyInput?: boolean
  resetValueOnBlur?: boolean
  inputId?: string
  filterOption?: Function

  // TODO: don't shadow existing DOM onChange definition
  onChange: Function
}

const HighlightFilter = ({ result = '', inputValue }) => (
  <div>
    {result.split(inputValue).map((p, i) =>
      i ? (
        <Fragment key={i}>
          <strong style={{ textDecoration: 'underline' }}>{inputValue}</strong>
          {p}
        </Fragment>
      ) : (
        <Fragment key={i}>{p}</Fragment>
      ),
    )}
  </div>
)

const MenuOption = ({ name, id, inputValue }) => (
  <>
    <HighlightFilter {...{ result: name, inputValue }} />
    <small>
      <HighlightFilter {...{ result: id, inputValue }} />
    </small>
  </>
)

const formatOptionLabel = ({ name, id, value }, { context, inputValue }) =>
  context === 'value' ? (
    <>{value}</>
  ) : (
    <MenuOption {...{ name, id, inputValue }} />
  )

const noOptionsMessage = ({ inputValue }) =>
  inputValue.length
    ? `No resources found containing '${inputValue}'`
    : 'Start typing…'

const ResourceSelect = (props: IResourceSelect) => {
  const {
    resource,
    onChange,
    allowAnyInput = false,
    resetValueOnBlur = true,
    inputId = 'resource',
    ...rest
  } = props

  // Keep input after blur event even if the option was not in the search results.
  const [inputValue, setInputValue] = useState()
  const handleInputChange = useCallback(
    (inputValue, { action }) => {
      if (action !== 'input-blur' && action !== 'menu-close') {
        setInputValue(inputValue)
        onChange({ value: inputValue })
      }
    },
    [setInputValue, onChange],
  )

  // when dropdown is not manually clicked then it does not scroll into view
  const onMenuOpen = () => {
    setTimeout(() => {
      const selectedEl = document.getElementsByClassName(
        'ResourcesDropdown__option--is-selected',
      )[0]
      if (selectedEl) {
        selectedEl.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'start',
        })
      }
    }, 15)
  }

  const loadOptions = async filter => {
    const resources = await findResources(filter)

    return isEmpty(resources) && allowAnyInput
      ? [{ name: filter, value: filter }]
      : resources
  }

  const selectProps = {
    inputId,
    cacheOptions: true,
    defaultValue: resource ? { id: resource, value: resource } : undefined,
    loadOptions,
    onChange: v => onChange(v || {}),
    isClearable: true,
    formatOptionLabel,
    noOptionsMessage,
    onMenuOpen,
    className: 'ResourcesDropdown',
    classNamePrefix: 'ResourcesDropdown',
    inputValue,
    onInputChange: resetValueOnBlur ? noop : handleInputChange,
    defaultOptions: true,
    ...rest,
  }
  return <AsyncSelect {...selectProps} />
}

export default ResourceSelect
